import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useAuthContext } from "@/context/AuthContext";
import {
  getWorkspaceValueChainImportance,
  listValueChainStages,
  putWorkspaceValueChainImportance,
  type ValueChainStage,
} from "@/lib/assets";

const RATING_LEGEND: Record<number, string> = {
  0: "Not applicable",
  1: "Low relevancy — activity continues almost as normal",
  2: "Moderate relevancy",
  3: "High relevancy",
  4: "Very high relevancy — activity would need to stop entirely",
};

const SECTOR_LABELS: Record<string, string> = {
  energy: "Energy",
  agriculture: "Agriculture",
};

function ValueChainImportanceForm() {
  const { workspace } = useAuthContext();
  const [stages, setStages] = useState<ValueChainStage[] | null>(null);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    Promise.all([listValueChainStages(), getWorkspaceValueChainImportance()])
      .then(([stagesResult, ratingsResult]) => {
        if (cancelled) return;
        setStages(stagesResult.stages);
        const initial: Record<string, number> = {};
        for (const stage of stagesResult.stages) {
          initial[stage.id] = 0;
        }
        for (const rating of ratingsResult.ratings) {
          initial[rating.valueChainStageId] = rating.importance;
        }
        setRatings(initial);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load value chain stages");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function toggleStage(stageId: string, checked: boolean) {
    setRatings((prev) => ({ ...prev, [stageId]: checked ? (prev[stageId] || 1) : 0 }));
  }

  function setRating(stageId: string, value: number) {
    setRatings((prev) => ({ ...prev, [stageId]: value }));
  }

  async function handleSave() {
    if (!stages) return;
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const payload = stages.map((stage) => ({
        valueChainStageId: stage.id,
        importance: (ratings[stage.id] ?? 0) as 0 | 1 | 2 | 3 | 4,
      }));
      await putWorkspaceValueChainImportance(payload);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save ratings");
    } finally {
      setSaving(false);
    }
  }

  const sectorLabel = workspace ? SECTOR_LABELS[workspace.sector] ?? workspace.sector : "";

  return (
    <Card id="value-chain-data">
      <CardHeader>
        <CardTitle>Value chain data — {sectorLabel} value chain</CardTitle>
        <p className="text-sm text-muted-foreground">
          Select the stages that apply to your business, then rate each one's importance. This
          applies once across your whole workspace, not per site.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!stages && !error && <p className="text-sm text-muted-foreground">Loading...</p>}
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

        {stages?.map((stage) => {
          const value = ratings[stage.id] ?? 0;
          return (
            <div key={stage.id} className="flex items-center justify-between gap-4 border-b border-border pb-3 last:border-0">
              <div className="flex items-center gap-2.5">
                <Checkbox
                  checked={value > 0}
                  onCheckedChange={(checked) => toggleStage(stage.id, checked === true)}
                />
                <div>
                  <p className="text-sm font-medium">{stage.name}</p>
                  {stage.description && <p className="text-xs text-muted-foreground">{stage.description}</p>}
                </div>
              </div>

              <div className="flex shrink-0 gap-1.5">
                {[0, 1, 2, 3, 4].map((level) => (
                  <button
                    key={level}
                    type="button"
                    title={RATING_LEGEND[level]}
                    onClick={() => setRating(stage.id, level)}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-full border text-xs font-medium transition-colors",
                      value === level
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-input text-muted-foreground hover:border-primary/50"
                    )}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {stages && (
          <>
            <p className="text-xs text-muted-foreground">
              0 = not applicable, 4 = very high relevancy (activity would need to stop entirely).
            </p>
            <div className="flex items-center gap-3">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
              {saved && <p className="text-sm font-medium text-primary">Saved.</p>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default ValueChainImportanceForm;
